/** Sidebar visibility for top-level nav that is not an entity catalog entry. */

export type PermissionCheck = (permission: string) => boolean

/** Reports that share document-view perms (batch / outbound) with day-to-day ops.
 * Without an explicit 报表/提醒 grant they would keep「报表分析」open after those
 * module permissions are revoked — Triio #75 retest. */
export const REPORTS_NEEDING_REPORTING_GRANT = [
  'location-map',
  'sales-daily',
  'safety',
  'expiry',
] as const

export type ReportNavItem = {
  key: string
  permission: string
  costPermission?: string
  amountPermission?: string
  navigation?: boolean
}

/** 审批工作台：only users who can read approval instances get the menu entry.
 * Deep links from documents remain reachable; the route itself stays open. */
export function canShowApprovalsNav(can: PermissionCheck): boolean {
  return can('workflow.view_approvalinstance')
}

/** One report row in「报表分析」— applies catalog permission plus location-map /
 * reporting-module gates so stripping 报表/提醒 actually clears the leftover items. */
export function canShowReportItem(
  item: ReportNavItem,
  can: PermissionCheck,
  options: { forNav?: boolean } = { forNav: true },
): boolean {
  if (options.forNav !== false && item.navigation === false) return false
  if (!can(item.permission)) return false
  if (item.costPermission && !can(item.costPermission)) return false
  if (item.amountPermission && !can(item.amountPermission)) return false
  if (item.key === 'location-map'
    && (!can('masterdata.view_location') || !can('masterdata.view_warehouse'))) return false
  if ((REPORTS_NEEDING_REPORTING_GRANT as readonly string[]).includes(item.key)
    && !can('reporting.view_reportingrule')
    && !can('reporting.view_reportingalert')) return false
  return true
}

/** 报表分析：hide the whole group when the user has no report, rule, or reminder grant.
 * Avoids an empty「报表分析」submenu. */
export function canShowReportsNav(
  can: PermissionCheck,
  visibleReportCount: number,
): boolean {
  return visibleReportCount > 0
    || can('reporting.view_reportingrule')
    || can('reporting.view_reportingalert')
}
